'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/Button';
import { validateContactForm, hasErrors, type FormErrors } from '@/lib/form-validation';
import { submitToHubSpot } from '@/lib/hubspot';
import { getAllTrackingData } from '@/lib/tracking';
import { getConsentSnapshot, getSavedConsent } from '@/lib/consent';
import Step3ContactNew from './FormSteps/Step3ContactNew';
import ReCaptchaLoader from './ReCaptchaLoader';
import styles from './ContactForm.module.css';

declare global {
  interface Window {
    grecaptcha?: {
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export interface ContactFormProps {
  locale: string;
  translations: any;
}

export interface FormData {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

export default function ContactForm({ locale, translations }: ContactFormProps) {
  const router = useRouter();
  
  // Extract the form translations
  const t = translations.contact?.form || translations.form || {};
  
  const [formData, setFormData] = useState<FormData>({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formStartTime] = useState(Date.now());
  const [honeypot, setHoneypot] = useState('');

  // Track form abandonment
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!isSubmitting && (formData.email || formData.firstname)) {
        // Track form abandonment event
        if (typeof window !== 'undefined' && window.dataLayer) {
          window.dataLayer.push({
            event: 'form_abandonment',
            form_type: 'contact',
          });
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formData, isSubmitting]);

  // Track page view
  useEffect(() => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'contact_page_view',
        page_type: 'contact',
      });
    }
  }, []);

  const handleSubmit = async () => {
    // Anti-spam: Timing check (must take at least 3 seconds)
    const timeSpent = Date.now() - formStartTime;
    if (timeSpent < 3000) {
      setSubmitError(t.errors?.tooFast || 'Even geduld alstublieft');
      return;
    }

    // Validate all fields
    const validationErrors = validateContactForm(formData);
    
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Get reCAPTCHA v3 token
      const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
      
      if (!recaptchaSiteKey || !window.grecaptcha) {
        throw new Error('reCAPTCHA not initialized');
      }

      const recaptchaToken = await window.grecaptcha.execute(recaptchaSiteKey, {
        action: 'contact_form',
      });

      // Get consent status
      const savedConsent = getSavedConsent();
      const hasAnalyticsConsent = savedConsent?.analytics || false;

      // Only get tracking data if analytics consent is granted (GDPR compliance)
      const trackingData = hasAnalyticsConsent ? getAllTrackingData() : {};

      // Get consent snapshot for GDPR audit trail (always included)
      const consentSnapshot = getConsentSnapshot();

      // Prepare complete form data with form_type parameter
      const completeFormData = {
        ...formData,
        ...trackingData,  // Only includes UTM, device, etc. if analytics consent granted
        consent_snapshot: consentSnapshot,  // Always included for audit trail
        page_title: document.title,  // Always included (not PII)
        form_type: 'contact',  // IMPORTANT: Distinguish from demo requests
        page_locale: locale,
      };

      // Submit via API route (server-side verification)
      const response = await fetch('/api/submit-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formData: completeFormData,
          recaptchaToken,
          honeypot,
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Track conversion with Enhanced Conversions data
        if (typeof window !== 'undefined') {
          // Import trackConversion dynamically
          import('@/lib/analytics').then(({ trackConversion }) => {
            trackConversion('contact_form', {
              email: formData.email,
              phone: formData.phone,
              firstName: formData.firstname,
              lastName: formData.lastname,
            });
          });
          
          // Also push detailed event to dataLayer
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'form_submit',
              form_type: 'contact',
              form_location: 'contact_page',
            });
          }
        }

        // Redirect to thank you page
        router.push(result.redirectUrl || `/${locale === 'en' ? '' : locale + '/'}thank-you`);
      } else {
        const errorMsg = t.errors?.submitFailed || 'Er ging iets mis. Probeer het later opnieuw.';
        const emailFallback = t.errors?.emailFallback || 'Je kunt ook direct mailen naar info@jamezz.nl';
        setSubmitError(`${errorMsg} ${emailFallback}`);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMsg = t.errors?.submitFailed || 'Er ging iets mis. Probeer het later opnieuw.';
      const emailFallback = t.errors?.emailFallback || 'Je kunt ook direct mailen naar info@jamezz.nl';
      setSubmitError(`${errorMsg} ${emailFallback}`);
      setIsSubmitting(false);
    }
  };

  const updateField = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  return (
    <div className={styles.formContainer}>
      {/* Load reCAPTCHA only on form pages */}
      <ReCaptchaLoader />

      {/* Honeypot field (hidden from users, trap for bots) */}
      <input
        type="text"
        name="website_url"
        id="website_field"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        autoComplete="off"
        tabIndex={-1}
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
        aria-hidden="true"
      />

      {/* Form content - Direct contact form without steps */}
      <div className={styles.formContent}>
        <Step3ContactNew
          formData={formData}
          onChange={updateField}
          errors={errors}
          translations={{ demoRequest: { form: t } }}
          locale={locale}
          hideTitle={true}
        />

        {/* Error message */}
        {submitError && (
          <div className={styles.errorMessage} role="alert">
            {submitError}
          </div>
        )}

        {/* Submit button */}
        <div className={styles.formActions}>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={styles.submitButton}
          >
            {isSubmitting ? (t.submitting || 'Bezig met verzenden...') : (t.submit || 'Verstuur Aanvraag')}
          </Button>
        </div>
      </div>
    </div>
  );
}


